/**
 * @file: A strip below the week grid that shows what reaches the screen.
 *
 * The operators read the week immediately after they build it. The result
 * must therefore be on the same surface, and not in a dialog that they must
 * open. The strip has one cell for each day, and each cell is cut where the
 * winner changes.
 *
 * The strip stands below the surface that holds the toolbar and the grid. It
 * is never inside the calendar element. The zoom code queries `.fc-scroller-liquid-absolute` and `.fc-timegrid-slot`
 * inside `calendar.el`, and an element of ours in there would match those
 * queries. The free core of FullCalendar also has no hook for a section below
 * the grid.
 */

import { sameIntervalId, inspectedIntervalId } from '../calendar-intervals'
import { readColumnBoxes, fractionOfDay } from '../grid-geometry'
import { escapeHtml } from 'common/dom-util'

/**
 * The words for a stretch with no winner. The legend and the tooltip of the
 * stretch use them, so both give the same name to the same mark.
 */
const GAP_TEXT = 'No interval on screen'

/**
 * The legend has one line of text for each mark that is not the playlist
 * colour. The sample class draws the mark with the same rule as the strip and
 * the bars.
 */
const LEGEND = Object.freeze([
  Object.freeze({ sample: 'on-screen-legend-swatch is-gap', text: GAP_TEXT }),
  Object.freeze({ sample: 'on-screen-legend-swatch is-covered', text: 'Another interval is on screen' }),
  Object.freeze({ sample: 'on-screen-legend-mark', text: 'Never on screen in this view' })
])

class OnScreenBand {
  constructor (controller) {
    this.controller = controller
    this.element = null
    this.track = null
  }

  /**
   * The element of the strip. The code creates it below the surface of the grid
   * on first use. The strip is on the ground of the page, outside the surface.
   */
  ensureElement (calendar) {
    if (this.element) return this.element

    const surface = calendar && calendar.el ? calendar.el.parentElement : null

    if (!surface || !surface.parentElement) return null

    this.element = document.createElement('div')
    this.element.className = 'on-screen-band'
    // The track has the full width, so a measured column position applies
    // directly. The name of the strip is on a row below the track. The gutter
    // beside the time axis is narrower than the word "screen", and it cut the
    // name to "ON / REEN".
    this.element.innerHTML =
      '<div class="on-screen-track"></div>' +
      '<div class="on-screen-legend">' +
      '<span class="jumbotron-kind-label">On screen</span>' +
      LEGEND.map(entry => '<span class="jumbotron-note on-screen-legend-entry">' +
        `<span class="${entry.sample}" aria-hidden="true"></span>${entry.text}</span>`).join('') +
      '</div>'
    surface.after(this.element)
    this.track = this.element.querySelector('.on-screen-track')

    return this.element
  }

  destroy () {
    if (this.element && this.element.parentElement) {
      this.element.parentElement.removeChild(this.element)
    }
    this.element = null
    this.track = null
  }

  /**
   * Measure the day columns.
   *
   * The axis width follows its content, and the body has a scrollbar that the
   * header does not have. The code therefore reads the positions from the
   * columns, and does not calculate them.
   */
  columns (calendar, origin) {
    const box = origin.getBoundingClientRect()

    // readColumnBoxes reports the viewport frame, which is what the right-click
    // menu uses. The track has its own origin, so the only thing
    // left here is the shift.
    return readColumnBoxes(calendar.el)
      .map(column => ({ ...column, left: column.left - box.left }))
  }

  render () {
    const calendar = this.controller.calendarManager && this.controller.calendarManager.calendar

    if (!calendar) return

    const element = this.ensureElement(calendar)

    if (!element) return

    // Show the element before the measurement. A hidden element has no box.
    element.hidden = false

    const columns = this.columns(calendar, this.track)

    if (columns.length === 0) {
      element.hidden = true
      this.track.innerHTML = ''
      return
    }

    // A run that a stored, inspected interval wins gets the selection mark of
    // the grid. The code reads the selection during the render, so the mark
    // stays correct after a refetch and after a zoom, with no extra state.
    const selectedId = inspectedIntervalId(this.controller)
    element.classList.toggle('has-selection', selectedId !== null)

    this.track.innerHTML = this.markup(columns, this.controller.calendarManager.week(), selectedId)
  }

  /**
   * @param {Array} columns - The measured day columns
   * @param {Object} week - The snapshot from CalendarManager.week
   * @param {number|string|null} selectedId - The interval that the panel shows
   */
  markup (columns, week, selectedId) {
    const cells = columns.map(column => {
      const segments = week.segmentsOn(column.date)

      const runs = segments.map(segment => {
        // The share of the day comes from the module the grid reads, so a run
        // lines up with the bars above it. The elapsed milliseconds of the day
        // were used before, and they are 23 or 25 hours on the two days each
        // year when the clock changes.
        const from = fractionOfDay(column.date, segment.start)
        const to = fractionOfDay(column.date, segment.end)

        if (from === null || to === null) {
          return ''
        }

        const left = from * 100
        const width = (to - from) * 100
        const place = `left: ${left.toFixed(3)}%; width: ${width.toFixed(3)}%`

        // A stretch with no winner gets its stripes from the LESS file, which
        // reads a token, so the stripes follow the theme.
        if (!segment.winner) {
          return `<span class="on-screen-run is-gap" title="${GAP_TEXT}" style="${place}"></span>`
        }

        const fill = segment.winner.colour || 'var(--jumbotron-primary)'
        const selected = sameIntervalId(segment.winner.id, selectedId)

        return `<span class="on-screen-run${selected ? ' is-selected' : ''}" ` +
          `title="${escapeHtml(segment.winner.title)}" ` +
          `style="${place}; background: ${fill}"></span>`
      }).join('')

      return '<span class="on-screen-cell" ' +
        `style="left: ${column.left.toFixed(1)}px; width: ${column.width.toFixed(1)}px">${runs}</span>`
    }).join('')

    return cells
  }
}

export { OnScreenBand }
